import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setAppLanguage } from "@/utils/language";
import { useTranslation } from "react-i18next";

import {
  detectCountry,
  getCurrencies,
  initializeSelectedCurrency,
  setSelectedCurrency,
} from "@/store/slices/currentCountrySlice";
import { getUserProfile, updateProfile } from "@/store/slices/userSlice";

export default function CountryDetection() {
  const dispatch = useDispatch();
  const { t } = useTranslation("country-detection");

  
  const { user } = useSelector((state) => state.user);

  const {
    currencies,
    detectedCurrency,
    selectedCurrency,
    loading,
    detectingCountry,
    error,
  } = useSelector(
    (state) => state.currentCurrency
  );

  const [showConfirmation, setShowConfirmation] =
    useState(false);

  const [showCountryList, setShowCountryList] =
    useState(false);

  const [showLanguageList, setShowLanguageList] =
    useState(false);

  const [selectedLanguage, setSelectedLanguage] =
    useState("en");

  /**
   * Prevent initialization from running
   * more than once.
   */
  const [initialized, setInitialized] =
    useState(false);

  /**
   * =========================================
   * INITIALIZE COUNTRY / CURRENCY
   * =========================================
   *
   * Flow:
   *
   * 1. Restore selected currency
   * 2. Check localStorage
   * 3. Fetch currencies
   * 4. Detect country
   * 5. Show confirmation
   */
  useEffect(() => {
    if (initialized) {
      return;
    }

    const initialize = async () => {
      /**
       * Mark initialization immediately.
       */
      setInitialized(true);

      /**
       * Restore currency from localStorage
       */
      dispatch(initializeSelectedCurrency());

      /**
       * Restore language
       */
      const savedLanguage =
        localStorage.getItem("app_language");

      if (savedLanguage) {
        setSelectedLanguage(
          savedLanguage
        );
      }

      /**
       * Check saved currency
       */
      const savedCurrency =
        localStorage.getItem(
          "selected_currency"
        );

      /**
       * If user already selected a currency,
       * don't show country detection again.
       */
      if (savedCurrency) {
        return;
      }

      /**
       * =========================================
       * FETCH CURRENCIES
       * =========================================
       */

      let currencyList = currencies;

        try {
          currencyList =
            await dispatch(
              getCurrencies()
            ).unwrap();

         
        } catch (err) {
          console.error(
            "Failed to fetch currencies:",
            err
          );

          return;
        }

      /**
       * =========================================
       * DETECT COUNTRY
       * =========================================
       *
       * At this point getCurrencies has
       * already completed.
       */
      if (
        !currencyList ||
        currencyList.length === 0
      ) {
        console.error(
          "No currencies available. Cannot detect country."
        );

        return;
      }

      try {
        const result =
          await dispatch(
            detectCountry()
          ).unwrap();

      } catch (err) {
        console.error(
          "Country detection failed:",
          err
        );
      }
    };

    initialize();
  }, [dispatch, initialized, currencies]);

  /**
   * =========================================
   * SHOW CONFIRMATION
   * =========================================
   *
   * Once Redux receives detectedCurrency,
   * open the modal.
   */
  useEffect(() => {

    if (
      detectedCurrency &&
      !selectedCurrency
    ) {

      setShowConfirmation(true);
    }
  }, [
    detectedCurrency,
    selectedCurrency,
    detectingCountry,
  ]);

  /**
   * =========================================
   * CONFIRM DETECTED COUNTRY
   * =========================================
   */
  const handleConfirmCountry = () => {
    if (!detectedCurrency) {
      return;
    }

    dispatch(
      setSelectedCurrency(
        detectedCurrency
      )
    );

    setShowConfirmation(false);

    setShowLanguageList(true);
  };

  /**
   * =========================================
   * CHANGE COUNTRY
   * =========================================
   */
  const handleChangeCountry = () => {
    setShowConfirmation(false);

    setShowCountryList(true);
  };

  /**
   * =========================================
   * SELECT COUNTRY
   * =========================================
   */
  const handleCountrySelect = (
    currency
  ) => {
    if (!currency) {
      return;
    }

    dispatch(
      setSelectedCurrency(currency)
    );

    setShowCountryList(false);

    setShowLanguageList(true);
  };

  /**
   * =========================================
   * SELECT LANGUAGE
   * =========================================
   */
  const handleLanguageSelect = async (
    language
  ) => {
    setSelectedLanguage(language);
  };

  /**
   * =========================================
   * LANGUAGE CONTINUE
   * =========================================
   */
  const handleLanguageContinue = async () => {

    await changeLanguageCurrency(
      selectedLanguage,
      selectedCurrency?.currency_code
    );

    setAppLanguage(
      selectedLanguage
    );

    localStorage.setItem(
      "app_language",
      selectedLanguage
    );

    setShowLanguageList(false);
  };

  /**
   * =========================================
   * COUNTRY CANCEL
   * =========================================
   */
  const handleCountryCancel = () => {
    setShowCountryList(false);

    if (detectedCurrency) {
      setShowConfirmation(true);
    }
  };

  /**
   * =========================================
   * COUNTRY CONFIRMATION MODAL
   * =========================================
   */
  {/*
    Don't return null here.
    The component should always render.
  */}

  const changeLanguageCurrency = async (lang, currency_code) => {
    if(user?.language === lang && user?.currency_code === currency_code) return;
      try {
        const payload = {
          id: user?.id,
          first_name: user?.first_name,
          email: user?.email,
          role: user?.roles[0],
          language: lang,
          currency_code: currency_code,
        };
  
        dispatch(updateProfile(payload)).unwrap().then((res)=>{
          if(res?.status){
            dispatch(getUserProfile());
          }
        });
      } catch (error) {
        console.error(error);
      }
  };

  return (
    <>
      {/* =========================================
          COUNTRY CONFIRMATION
      ========================================== */}

      {showConfirmation &&
        detectedCurrency && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-2xl">
              <div className="text-center">

                <div className="mb-4 text-6xl">
                  {detectedCurrency.flag ||
                    "🌍"}
                </div>

                <h2 className="text-2xl font-bold text-gray-800">
                  {t("country_detection.is_this_your_country")}
                </h2>

                <p className="mt-3 text-gray-600">
                  {t("country_detection.we_detected_your_country_as")}
                </p>

                <p className="mt-2 text-xl font-bold text-gray-900">
                  {detectedCurrency.country_name ||
                    t("country_detection.unknown_country")}
                </p>

                <p className="mt-2 text-gray-500">
                  {t("country_detection.currency")}:{" "}
                  <strong>
                    {detectedCurrency.currency_code || "-"}
                  </strong>
                </p>

              </div>

              <div className="mt-8 flex gap-4">

                <button
                  type="button"
                  onClick={handleChangeCountry}
                  className="flex-1 rounded-md border border-gray-300 px-4 py-3 font-medium text-gray-700 hover:bg-gray-100"
                >
                  {t("country_detection.change_country")}
                </button>

                <button
                  type="button"
                  onClick={handleConfirmCountry}
                  className="flex-1 rounded-md bg-yellow-500 px-4 py-3 font-bold text-white hover:bg-yellow-600"
                >
                  {t("country_detection.continue")}
                </button>

              </div>
            </div>
          </div>
        )}

      {/* =========================================
          COUNTRY LIST
      ========================================== */}

      {showCountryList && (
      <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">

          <div className="flex items-center justify-between">

            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {t("country_detection.select_your_country")}
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                {t("country_detection.select_country_description")}
              </p>
            </div>

            <button
              type="button"
              onClick={handleCountryCancel}
              className="text-xl text-gray-500 hover:text-gray-800"
            >
              ✕
            </button>

          </div>

          <div className="mt-5 max-h-80 space-y-2 overflow-y-auto">

            {loading && (
              <div className="py-5 text-center text-gray-500">
                {t("country_detection.loading_countries")}
              </div>
            )}

            {!loading &&
              currencies?.map((currency) => (
                <button
                  key={currency.id}
                  type="button"
                  onClick={() =>
                    handleCountrySelect(currency)
                  }
                  className="flex w-full items-center justify-between rounded-lg border border-gray-200 p-3 hover:bg-gray-100"
                >

                  <div className="flex items-center gap-3">

                    <span className="text-2xl">
                      {currency.flag || "🌍"}
                    </span>

                    <div className="text-left">

                      <p className="font-semibold text-gray-800">
                        {currency.country_name}
                      </p>

                      <p className="text-sm text-gray-500">
                        {currency.currency_code} -{" "}
                        {currency.currency_name}
                      </p>

                    </div>

                  </div>

                  {selectedCurrency?.id === currency.id && (
                    <span className="font-bold text-green-600">
                      ✓
                    </span>
                  )}

                </button>
              ))}

            {!loading &&
              (!currencies || currencies.length === 0) && (
                <div className="py-5 text-center text-gray-500">
                  {t("country_detection.no_countries_available")}
                </div>
              )}

          </div>

          <button
            type="button"
            onClick={handleCountryCancel}
            className="mt-5 w-full rounded-md border border-gray-300 px-4 py-3 font-medium text-gray-700 hover:bg-gray-100"
          >
            {t("country_detection.cancel")}
          </button>

        </div>
      </div>
    )}

      {/* =========================================
          LANGUAGE LIST
      ========================================== */}

      {showLanguageList && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">

            <div className="text-center">

              <div className="mb-4 text-5xl">
                🌐
              </div>

              <h2 className="text-xl font-bold text-gray-800">
                {t("country_detection.select_your_language")}
              </h2>

              <p className="mt-2 text-sm text-gray-500">
                {t("country_detection.select_language_description")}
              </p>

            </div>

            <div className="mt-6 space-y-3">

              {/* ENGLISH */}

              <button
                type="button"
                onClick={() =>
                  handleLanguageSelect("en")
                }
                className={`flex w-full items-center justify-between rounded-lg border p-4 ${
                  selectedLanguage ===
                  "en"
                    ? "border-green-600 bg-green-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <span className="font-medium">
                  🇬🇧 English
                </span>

                {selectedLanguage ===
                  "en" && (
                  <span className="font-bold text-green-600">
                    ✓
                  </span>
                )}
              </button>

              {/* ARABIC */}

              <button
                type="button"
                onClick={() =>
                  handleLanguageSelect("ar")
                }
                className={`flex w-full items-center justify-between rounded-lg border p-4 ${
                  selectedLanguage ===
                  "ar"
                    ? "border-green-600 bg-green-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <span className="font-medium">
                  🇸🇦 Arabic
                </span>

                {selectedLanguage ===
                  "ar" && (
                  <span className="font-bold text-green-600">
                    ✓
                  </span>
                )}
              </button>

            </div>

            <button
              type="button"
              onClick={handleLanguageContinue}
              className="mt-6 w-full rounded-md bg-yellow-500 px-4 py-3 font-bold text-white hover:bg-yellow-600"
            >
              {t("country_detection.continue")}
            </button>

          </div>
        </div>
      )}
    </>
  );
}